import React, { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../context/AuthContext';
import { pingExtension, shareReel, toAbsoluteUrl } from '../services/extensionBridge';
import {
  extractApiError,
  formatCountdown,
  parseUtcMs,
  promotionService,
  ShareInfo,
  ShareItem,
  SharePlatform,
} from '../services/promotionService';

import { PortraitReelPreview } from './PortraitReelPreview';
import {
  CreateReelBlock,
  DEFAULT_REEL_HIGHLIGHT_PROMPT,
  RegenerateHighlightControl,
} from './ReelHighlightPrompt';
import { ShareCooldownNotice } from './ShareCooldownNotice';
import {
  ReelsLoadingState,
  ShareHistoryView,
  ShareWorkspace,
  ShareWorkspaceSwitcher,
  ViewClaimControl,
} from './ShareEarnHistory';

type ShareStep = 'idle' | 'creating' | 'generating' | 'ready' | 'posting' | 'posted' | 'failed';

interface FlowState {
  step: ShareStep;
  share: ShareItem | null;
  error: string | null;
  manual: boolean;
  /** Platform chosen at post time — null until a post button is clicked. */
  platform: SharePlatform | null;
}

type SetFlow = React.Dispatch<React.SetStateAction<FlowState>>;

const IDLE_FLOW: FlowState = { step: 'idle', share: null, error: null, manual: false, platform: null };

const POLL_INTERVAL_MS = 1500;
const INSTALL_CHECK_INTERVAL_MS = 5000;
const COUNTDOWN_REFRESH_MS = 30000;

/* ── Hooks ── */

/** Detects the Snap & Share extension, re-checking every few seconds until it is found. */
export const useExtensionInstalled = (): boolean => {
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (installed) return undefined;

    let cancelled = false;

    const check = () => {
      pingExtension().then((ok) => {
        if (ok && !cancelled) setInstalled(true);
      });
    };

    check();
    const timer = window.setInterval(check, INSTALL_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [installed]);

  return installed;
};

/** Current time, refreshed on an interval — used for countdown labels. */
export const useNow = (intervalMs: number): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);

    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return now;
};

interface ShareInfoState {
  info: ShareInfo | null;
  infoError: string | null;
  refresh: () => void;
}

const useShareInfo = (): ShareInfoState => {
  const [info, setInfo] = useState<ShareInfo | null>(() => promotionService.getCachedShareInfo());
  const [infoError, setInfoError] = useState<string | null>(null);

  const load = useCallback((force: boolean) => {
    promotionService
      .getShareInfo(force)
      .then((data) => {
        setInfo(data);
        setInfoError(null);
      })
      .catch((err) => {
        console.error('Failed to fetch share info', err);
        setInfoError(extractApiError(err, 'Could not load reel view rewards.'));
      });
  }, []);

  const refresh = useCallback(() => load(true), [load]);

  useEffect(() => {
    load(false);
  }, [load]);

  return { info, infoError, refresh };
};

const useShareEarnContext = () => {
  const { user } = useAuth();

  return {
    shareInfo: useShareInfo(),
    installed: useExtensionInstalled(),
    isSystemAdmin: user?.system_role === 'system_admin',
  };
};

/**
 * Newest share OF THE CURRENT PROJECT when it is still mid-flow: generating,
 * or video ready but not yet posted. Reels are project-specific — a ready
 * reel from another project must not hijack this project's flow (it stays
 * visible in the shares list instead).
 */
const resumableShare = (info: ShareInfo | null, projectId?: string): ShareItem | null => {
  if (!projectId) return null;
  const latest = info?.shares.find(
    (share) => share.project_id === projectId && (
      share.status === 'pending_video' ||
      (share.status === 'video_ready' && !share.reel_url && !share.posted_at)
    ),
  );

  if (!latest) return null;
  if (latest.status === 'pending_video') return latest;
  if (latest.status === 'video_ready' && !latest.reel_url && !latest.posted_at) return latest;

  return null;
};

/**
 * Seeds the flow from an in-flight share so closing/reopening the modal (or a
 * page refresh) resumes generation polling / the ready-to-post view instead of
 * stranding the share. Returns a dismiss() that resets the flow and prevents
 * re-seeding the same share.
 */
const useResumeShare = (
  info: ShareInfo | null,
  flow: FlowState,
  setFlow: SetFlow,
  setHighlightPrompt: React.Dispatch<React.SetStateAction<string>>,
  projectId?: string,
): (() => void) => {
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const shareId = flow.share?.id ?? null;
  const idle = flow.step === 'idle' && !flow.share;

  const dismiss = useCallback(() => {
    setDismissedId(shareId);
    setFlow(IDLE_FLOW);
  }, [shareId, setFlow]);

  useEffect(() => {
    if (!idle) return;

    const share = resumableShare(info, projectId);

    if (!share || share.id === dismissedId) return;

    setFlow({
      step: share.status === 'pending_video' ? 'generating' : 'ready',
      share,
      error: null,
      manual: false,
      platform: null,
    });
    setHighlightPrompt(share.highlight_prompt ?? DEFAULT_REEL_HIGHLIGHT_PROMPT);
  }, [idle, info, dismissedId, setFlow, setHighlightPrompt, projectId]);

  return dismiss;
};

/* ── Flow actions ── */

const startShare = async (
  projectId: string,
  highlightPrompt: string,
  setFlow: SetFlow,
  refresh: () => void,
) => {
  setFlow({ step: 'creating', share: null, error: null, manual: false, platform: null });

  try {
    const share = await promotionService.createShare(projectId, highlightPrompt.trim());

    setFlow({ step: 'generating', share, error: null, manual: false, platform: null });
  } catch (err) {
    setFlow({ ...IDLE_FLOW, error: extractApiError(err, 'Could not start the share. Please try again.') });
    refresh();
  }
};

/** Cancels an in-flight reel; resolves to false when the request failed. */
const cancelActiveShare = async (
  shareId: string,
  setFlow: SetFlow,
  onReset: () => void,
  refresh: () => void,
): Promise<boolean> => {
  try {
    await promotionService.cancelShare(shareId);

    // onReset (useResumeShare's dismiss) records the share id so stale
    // share-info cannot re-seed the just-cancelled share before refresh lands.
    onReset();
    refresh();

    return true;
  } catch (err) {
    setFlow((prev) => ({ ...prev, error: extractApiError(err, 'Could not cancel the reel. Please try again.') }));

    return false;
  }
};

const applyPolledShare = (share: ShareItem, setFlow: SetFlow, onCancelled: () => void, refresh: () => void) => {
  if (share.status === 'cancelled') {
    // Cancelled elsewhere (e.g. another tab) — drop back to idle; the slot is freed.
    onCancelled();
    refresh();
  } else if (share.status === 'video_ready') {
    setFlow({ step: 'ready', share, error: null, manual: false, platform: null });
  } else if (share.status === 'failed') {
    setFlow({
      step: 'failed',
      share,
      error: 'We could not generate your reel. Please try again later.',
      manual: false,
      platform: null,
    });
  } else {
    // Still generating — keep the flow but refresh progress/progress_stage.
    setFlow((prev) => (prev.step === 'generating' && prev.share?.id === share.id ? { ...prev, share } : prev));
  }
};

const pollShare = (shareId: string, setFlow: SetFlow, onCancelled: () => void, refresh: () => void) => {
  promotionService
    .getShare(shareId)
    .then((share) => applyPolledShare(share, setFlow, onCancelled, refresh))
    .catch(() => undefined);
};

const useSharePolling = (flow: FlowState, setFlow: SetFlow, onCancelled: () => void, refresh: () => void): void => {
  const shareId = flow.step === 'generating' && flow.share ? flow.share.id : null;

  useEffect(() => {
    if (!shareId) return undefined;

    const timer = window.setInterval(() => pollShare(shareId, setFlow, onCancelled, refresh), POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [shareId, setFlow, onCancelled, refresh]);
};

/** Requeues an unposted ready reel; resolves to false when the request failed. */
const regenerateActiveShare = async (
  shareId: string,
  highlightPrompt: string,
  setFlow: SetFlow,
): Promise<boolean> => {
  try {
    const share = await promotionService.regenerateShare(shareId, highlightPrompt.trim());

    setFlow({ step: 'generating', share, error: null, manual: false, platform: null });

    return true;
  } catch (err) {
    setFlow((prev) => ({ ...prev, error: extractApiError(err, 'Could not regenerate the reel. Please try again.') }));

    return false;
  }
};

const finalizePost = async (
  share: ShareItem,
  reelUrl: string,
  platform: SharePlatform | null,
  setFlow: SetFlow,
  refresh: () => void,
) => {
  try {
    const updated = await promotionService.markPosted(share.id, reelUrl, platform ?? undefined);

    setFlow({ step: 'posted', share: updated, error: null, manual: false, platform });
    refresh();
  } catch (err) {
    setFlow({
      step: 'ready',
      share,
      error: extractApiError(err, 'Could not record your post.'),
      manual: true,
      platform,
    });
  }
};

const createAutomationToken = async (shareId: string): Promise<string | undefined> => {
  try {
    const session = await promotionService.createAutomationSession(shareId);

    return session.automation_token;
  } catch (err) {
    // AI guidance is an enhancement. The extension retains bounded semantic
    // fallbacks so a temporary model/session failure does not block sharing.
    console.warn('Could not start AI-guided share automation; using extension fallbacks', err);

    return undefined;
  }
};

const postReel = async (share: ShareItem, platform: SharePlatform, setFlow: SetFlow, refresh: () => void) => {
  if (!share.video_url) return;

  setFlow({ step: 'posting', share, error: null, manual: false, platform });
  const automationToken = await createAutomationToken(share.id);

  const result = await shareReel({
    shareId: share.id,
    platform,
    videoUrl: toAbsoluteUrl(share.video_url),
    caption: share.caption || '',
    automationToken,
    autoSubmit: true,
    openInBackground: platform === 'linkedin' && Boolean(automationToken),
  });

  if (result.ok && result.reelUrl) {
    await finalizePost(share, result.reelUrl, platform, setFlow, refresh);
  } else {
    setFlow({
      step: 'ready',
      share,
      error: result.error || 'The extension could not finish the post. You can post manually below.',
      manual: true,
      platform,
    });
  }
};

/* ── Presentational pieces ── */

const InstallExtensionRow: React.FC<{ installed: boolean; installUrl?: string }> = ({ installed, installUrl }) => (
  <div className="promo-extension-row">
    <span className="promo-extension-name">Chrome extension</span>
    {installed ? (
      <span className="promo-installed">Installed ✓</span>
    ) : (
      <a className="promo-install-btn" href={installUrl || '#'} rel="noreferrer" target="_blank">
        Install Extension
      </a>
    )}
  </div>
);

const LinkedInIcon: React.FC = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.55V9h3.57v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const clampProgress = (value: number): number => Math.min(Math.max(value, 0), 100);

const ReelProgressBar: React.FC<{ share: ShareItem | null; starting: boolean; cancel?: React.ReactNode }> = ({
  share,
  starting,
  cancel,
}) => {
  const progress = clampProgress(share?.progress ?? 0);
  const label = starting ? 'Starting…' : share?.progress_stage || 'Preparing your reel…';

  return (
    <>
      <div className="promo-progress-meta">
        <span className="promo-progress-label">{label}</span>
        {!starting && <span className="promo-progress-pct">{progress}%</span>}
        {cancel}
      </div>
      <div className={`promo-progress-track${starting ? ' promo-progress--indeterminate' : ''}`}>
        {!starting && <div className="promo-progress-fill" style={{ width: `${progress}%` }} />}
      </div>
    </>
  );
};

const CancelShareButton: React.FC<{
  shareId: string;
  setFlow: SetFlow;
  onReset: () => void;
  refresh: () => void;
}> = ({ shareId, setFlow, onReset, refresh }) => {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    const cancelled = await cancelActiveShare(shareId, setFlow, onReset, refresh);

    if (!cancelled) setCancelling(false);
  };

  return (
    <button
      aria-label="Cancel reel generation"
      className="promo-cancel-x"
      disabled={cancelling}
      onClick={handleCancel}
      title="Cancel"
      type="button"
    >
      ×
    </button>
  );
};

const ReelProgress: React.FC<{ flow: FlowState; setFlow: SetFlow; dismiss: () => void; refresh: () => void }> = ({
  flow,
  setFlow,
  dismiss,
  refresh,
}) => {
  const { share } = flow;
  const starting = flow.step === 'creating' || !share;

  return (
    <div className="promo-progress">
      <ReelProgressBar
        cancel={
          !starting && share ? (
            <CancelShareButton onReset={dismiss} refresh={refresh} setFlow={setFlow} shareId={share.id} />
          ) : null
        }
        share={share}
        starting={starting}
      />
      {flow.error && <div className="promo-error">{flow.error}</div>}
    </div>
  );
};

const CopyCaptionButton: React.FC<{ caption: string }> = ({ caption }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      console.error('Failed to copy caption');
    }
  };

  return (
    <button className={`promo-copy-btn${copied ? ' copied' : ''}`} onClick={handleCopy} type="button">
      {copied ? '✓ Copied' : 'Copy caption'}
    </button>
  );
};

const ManualFallback: React.FC<{
  share: ShareItem;
  platform: SharePlatform | null;
  setFlow: SetFlow;
  refresh: () => void;
}> = ({ share, platform, setFlow, refresh }) => {
  const [reelUrl, setReelUrl] = useState('');

  return (
    <div className="promo-manual">
      <div className="promo-manual-title">Post it manually</div>
      <div className="promo-manual-actions">
        {share.video_url && (
          <a className="promo-manual-link" download href={toAbsoluteUrl(share.video_url)} rel="noreferrer" target="_blank">
            Download video
          </a>
        )}
        <CopyCaptionButton caption={share.caption || ''} />
      </div>
      <div className="promo-manual-submit-row">
        <input
          className="promo-url-input"
          onChange={(e) => setReelUrl(e.target.value)}
          placeholder="Paste the URL of your post"
          type="text"
          value={reelUrl}
        />
        <button
          className="promo-manual-submit"
          disabled={!reelUrl.trim()}
          onClick={() => finalizePost(share, reelUrl.trim(), platform, setFlow, refresh)}
          type="button"
        >
          I posted it
        </button>
      </div>
    </div>
  );
};

interface ReadyViewProps {
  flow: FlowState;
  setFlow: SetFlow;
  refresh: () => void;
  share: ShareItem;
  eligible: boolean;
  nextShareAt: string | null;
  highlightPrompt: string;
  onHighlightPromptChange: (highlightPrompt: string) => void;
}

const postButtonLabel = (flow: FlowState): string => (flow.step === 'posting' && flow.platform === 'linkedin'
  ? 'Posting…'
  : 'Post to LinkedIn');

type PostButtonsProps = Pick<
  ReadyViewProps,
  'eligible' | 'flow' | 'nextShareAt' | 'refresh' | 'setFlow' | 'share'
>;

const PostButtons: React.FC<PostButtonsProps> = ({
  eligible,
  flow,
  nextShareAt,
  setFlow,
  refresh,
  share,
}) => {
  const now = useNow(COUNTDOWN_REFRESH_MS);
  const locked = !eligible && Boolean(nextShareAt) && (nextShareAt ? parseUtcMs(nextShareAt) > now : false);
  const disabled = flow.step === 'posting' || locked;

  return (
    <>
      {locked && (
        <div className="promo-submit-lock">
          Next submission unlocks {formatCountdown(nextShareAt, now)}.
        </div>
      )}
      <div className="promo-platforms">
        <button
          className="promo-platform-btn promo-platform-btn--linkedin"
          disabled={disabled}
          onClick={() => postReel(share, 'linkedin', setFlow, refresh)}
          type="button"
        >
          <LinkedInIcon /> {postButtonLabel(flow)}
        </button>
      </div>
    </>
  );
};

const ReadyView: React.FC<ReadyViewProps> = ({
  eligible,
  flow,
  highlightPrompt,
  nextShareAt,
  onHighlightPromptChange,
  setFlow,
  refresh,
  share,
}) => (
  <div className={`promo-preview${share.video_url ? '' : ' promo-preview--no-video'}`}>
    {share.video_url && <PortraitReelPreview src={toAbsoluteUrl(share.video_url)} />}
    <div className="promo-preview-details">
      {share.caption && <div className="promo-caption">{share.caption}</div>}
      <PostButtons
        eligible={eligible}
        flow={flow}
        nextShareAt={nextShareAt}
        refresh={refresh}
        setFlow={setFlow}
        share={share}
      />
      <RegenerateHighlightControl
        disabled={flow.step === 'posting'}
        highlightPrompt={highlightPrompt}
        onHighlightPromptChange={onHighlightPromptChange}
        onRegenerate={(revisedPrompt) => regenerateActiveShare(share.id, revisedPrompt, setFlow)}
      />
      {flow.error && <div className="promo-error">{flow.error}</div>}
      {flow.manual && <ManualFallback platform={flow.platform} refresh={refresh} setFlow={setFlow} share={share} />}
    </div>
  </div>
);
const FailedView: React.FC<{ flow: FlowState; onReset: () => void }> = ({ flow, onReset }) => (
  <div className="promo-failed">
    <div className="promo-error">{flow.error || 'Reel generation failed.'}</div>
    <button className="promo-retry-btn" onClick={onReset} type="button">
      Back
    </button>
  </div>
);

const SuccessView: React.FC<{
  installed: boolean;
  isSystemAdmin: boolean;
  share: ShareItem | null;
  onReset: () => void;
  refresh: () => void;
}> = ({ installed, isSystemAdmin, share, onReset, refresh }) => (
  <div className="promo-success">
    <div className="promo-success-title">Reel posted!</div>
    <div className="promo-success-text">
      View credits unlock 24 hours after posting. Claim them here once we can verify your reel&apos;s views.
    </div>
    {share?.reel_url && (
      <a className="promo-manual-link" href={share.reel_url} rel="noreferrer" target="_blank">
        View your post
      </a>
    )}
    {share && (
      <ViewClaimControl
        installed={installed}
        isSystemAdmin={isSystemAdmin}
        refresh={refresh}
        share={share}
      />
    )}
    <button className="promo-retry-btn" onClick={onReset} type="button">
      Done
    </button>
  </div>
);

const ShareInfoErrorRow: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="promo-failed">
    <div className="promo-error">{message}</div>
    <button className="promo-retry-btn" onClick={onRetry} type="button">
      Retry
    </button>
  </div>
);

const ShareStats: React.FC<{ info: ShareInfo | null }> = ({ info }) => (
  <dl aria-label="Reel summary" className="referral-stats promo-stats">
    <div className="stat-card">
      <dt className="stat-label">Reels shared</dt>
      <dd className="stat-value">{info?.total_shares_posted ?? '-'}</dd>
    </div>
    <div className="stat-card">
      <dt className="stat-label">View credits</dt>
      <dd className="stat-value stat-value--amber">{info?.total_share_credits ?? '-'}</dd>
    </div>
  </dl>
);

/* ── Flow composition ── */

interface ShareFlowProps {
  projectId?: string;
  info: ShareInfo | null;
  flow: FlowState;
  setFlow: SetFlow;
  refresh: () => void;
  dismiss: () => void;
  installed: boolean;
  isSystemAdmin: boolean;
  highlightPrompt: string;
  onHighlightPromptChange: (highlightPrompt: string) => void;
}

const ActiveShareFlow: React.FC<ShareFlowProps> = ({
  dismiss,
  flow,
  highlightPrompt,
  info,
  installed,
  isSystemAdmin,
  onHighlightPromptChange,
  setFlow,
  refresh,
}) => {
  if (flow.step === 'creating' || flow.step === 'generating') {
    return <ReelProgress dismiss={dismiss} flow={flow} refresh={refresh} setFlow={setFlow} />;
  }

  if (flow.step === 'failed') return <FailedView flow={flow} onReset={dismiss} />;

  return flow.step === 'posted' || !flow.share ? (
    <SuccessView
      installed={installed}
      isSystemAdmin={isSystemAdmin}
      onReset={dismiss}
      refresh={refresh}
      share={flow.share}
    />
  ) : (
    <ReadyView
      eligible={Boolean(info?.eligible)}
      flow={flow}
      highlightPrompt={highlightPrompt}
      nextShareAt={info?.next_share_at ?? null}
      onHighlightPromptChange={onHighlightPromptChange}
      refresh={refresh}
      setFlow={setFlow}
      share={flow.share}
    />
  );
};

const ShareFlow: React.FC<ShareFlowProps> = (props) => {
  const { projectId } = props;

  // An active (possibly resumed) share renders even without a current project.
  if (props.flow.step !== 'idle') return <ActiveShareFlow {...props} />;

  if (!props.info) return <ReelsLoadingState />;

  if (!projectId) return <div className="promo-note">Open a project to create and share its promo reel.</div>;

  return !props.info.eligible ? (
    <ShareCooldownNotice
      isSystemAdmin={props.isSystemAdmin}
      nextShareAt={props.info.next_share_at}
      refresh={props.refresh}
    />
  ) : (
    <CreateReelBlock
      disabled={!props.info}
      error={props.flow.error}
      highlightPrompt={props.highlightPrompt}
      onCreate={(highlightPrompt) => startShare(projectId, highlightPrompt, props.setFlow, props.refresh)}
      onHighlightPromptChange={props.onHighlightPromptChange}
    />
  );
};

const useHistoricalShareSelection = (
  setFlow: SetFlow,
  setHighlightPrompt: React.Dispatch<React.SetStateAction<string>>,
  setWorkspace: (value: ShareWorkspace) => void,
) => (
  useCallback((share: ShareItem) => {
    setHighlightPrompt(share.highlight_prompt ?? DEFAULT_REEL_HIGHLIGHT_PROMPT);
    setFlow({ step: 'ready', share, error: null, manual: false, platform: null });
    setWorkspace('create');
  }, [setFlow, setHighlightPrompt, setWorkspace])
);

const useActiveShareState = (info: ShareInfo | null, projectId: string | undefined, refresh: () => void) => {
  const [flow, setFlow] = useState<FlowState>(IDLE_FLOW);
  const [highlightPrompt, setHighlightPrompt] = useState(DEFAULT_REEL_HIGHLIGHT_PROMPT);
  const dismiss = useResumeShare(info, flow, setFlow, setHighlightPrompt, projectId);

  useSharePolling(flow, setFlow, dismiss, refresh);

  return { dismiss, flow, highlightPrompt, setFlow, setHighlightPrompt };
};

export const ShareEarnSection: React.FC<{ projectId?: string }> = ({ projectId }) => {
  const { shareInfo: { info, infoError, refresh }, installed, isSystemAdmin } = useShareEarnContext();
  const { dismiss, flow, highlightPrompt, setFlow, setHighlightPrompt } = useActiveShareState(info, projectId, refresh);
  const [workspace, setWorkspace] = useState<ShareWorkspace>('create');
  const selectHistoricalShare = useHistoricalShareSelection(setFlow, setHighlightPrompt, setWorkspace);

  return (
    <div className={`promo-section promo-section--${workspace}`}>
      <ShareStats info={info} />
      <ShareWorkspaceSwitcher active={workspace} count={info?.shares.length ?? 0} onChange={setWorkspace} />
      {workspace === 'create' ? (
        <div className="promo-create-view">
          {!installed && <InstallExtensionRow installed={installed} installUrl={info?.extension_install_url} />}
          {infoError && !info ? (
            <ShareInfoErrorRow message={infoError} onRetry={refresh} />
          ) : (
            <ShareFlow
              dismiss={dismiss}
              flow={flow}
              highlightPrompt={highlightPrompt}
              info={info}
              installed={installed}
              isSystemAdmin={isSystemAdmin}
              projectId={projectId}
              onHighlightPromptChange={setHighlightPrompt}
              refresh={refresh}
              setFlow={setFlow}
            />
          )}
        </div>
      ) : (
        <ShareHistoryView
          info={info}
          installed={installed}
          isSystemAdmin={isSystemAdmin}
          onSelect={selectHistoricalShare}
          refresh={refresh}
          selectedShareId={flow.step === 'ready' || flow.step === 'posting' ? flow.share?.id ?? null : null}
          selectionDisabled={flow.step === 'creating' || flow.step === 'generating' || flow.step === 'posting'}
        />
      )}
    </div>
  );
};
